import { z } from 'zod';

/**
 * Maps rank geo-grid: `size × size` points spread around the listing, one Google Maps search per point. How
 * far the grid reaches and the search zoom come from the salon's scan area (`RANK_AREAS`). A listing missing
 * from the results is stored as null and scored as `notFoundRank`; anything worse than `displayCap` is shown
 * as "20+".
 */
export const RANK_GRID = { size: 7, notFoundRank: 21, displayCap: 20 } as const;

/**
 * Street map under the grid: the image is the grid's square area plus a plain map strip above and below,
 * each `attributionStripRatio` × the square's width, so Google's logo (bottom-left) and copyright
 * (bottom-right) are never covered by the dots — Google's terms require them visible. API (image size) and
 * web (layout) both read this one number.
 */
export const RANK_MAP = { attributionStripRatio: 0.11 } as const;

/**
 * `max` is the absolute ceiling (the biggest plan's keyword count); a salon's own limit is its plan's
 * `rankKeywordLimit` (billing catalog), which the API sends with the keyword list.
 */
export const RANK_KEYWORD_LIMITS = { max: 10, maxLength: 80 } as const;

/**
 * Nail salons' pre-filled keywords at setup; the owner can edit them (up to the plan's keyword limit). Other kinds
 * get theirs from `defaultRankKeywordsFor` (@repo/shared/business-kind).
 */
export const DEFAULT_RANK_KEYWORDS = ['nail salon', 'nail salon near me', 'pedicure', 'gel manicure'] as const;

/** Keywords are stored lower-cased with collapsed whitespace so "Nail  Salon" and "nail salon" are one row. */
export function normalizeRankKeyword(raw: string): string {
  return raw.trim().replace(/\s+/g, ' ').toLowerCase();
}

export const rankKeywordSchema = z
  .string()
  .transform(normalizeRankKeyword)
  .pipe(z.string().min(1).max(RANK_KEYWORD_LIMITS.maxLength));

/** Google Business Profile limits we validate against before calling Google. */
export const GBP_LIMITS = {
  /** `profile.description` — Google shows the first 250 chars before "More". */
  descriptionMax: 750,
  descriptionVisibleChars: 250,
  postSummaryMax: 1500,
  /** Review reply `comment` limit is in BYTES (UTF-8), not characters. */
  replyMaxBytes: 4096,
  /** Business Information API edit quota per location. */
  editsPerMinute: 10,
  /** Primary + additional categories ≤ 10 in total. */
  additionalCategoriesMax: 9,
  serviceItemNameMax: 140,
  serviceItemDescriptionMax: 250,
} as const;

/**
 * Which reviews auto-reply posts on: the lowest star rating covered (4 = 4–5★, 3 = 3–5★, 1 = all reviews).
 * Stored as `OrganizationMarketingSettings.reviewAutoReplyMinStars`.
 */
export const REVIEW_REPLY_AUTO_MIN_STARS_OPTIONS = [4, 3, 1] as const;
export type ReviewReplyAutoMinStars = (typeof REVIEW_REPLY_AUTO_MIN_STARS_OPTIONS)[number];
export const REVIEW_REPLY_AUTO_DEFAULT_MIN_STARS: ReviewReplyAutoMinStars = 4;

/**
 * Why a reply draft is held for a person even when auto-reply covers its stars
 * (`GoogleReviewReplyDraft.holdReason`). Risky reviews must never get an automatic public reply.
 */
export const REVIEW_REPLY_HOLD_REASONS = [
  'health_safety',
  'legal',
  'discrimination_harassment',
  'money',
  'unclear',
  'question',
] as const;
export type ReviewReplyHoldReason = (typeof REVIEW_REPLY_HOLD_REASONS)[number];

/** Ranges for the review-reply settings (`OrganizationMarketingSettings`), validated by API and web alike. */
export const REVIEW_REPLY_SETTINGS_LIMITS = {
  signatureMax: 80,
  monthlyReviewGoal: { min: 1, max: 500 },
} as const;

/**
 * Ranges for the Google posts autopilot settings. Weekday 0 = Sunday, hour is salon-local. With 2 posts a
 * week the second slot falls `secondPostDayOffset` days after `gbpPostWeekday` (same hour).
 */
export const GBP_POST_SETTINGS_LIMITS = {
  postsPerWeek: { min: 1, max: 2 },
  weekday: { min: 0, max: 6 },
  hourLocal: { min: 0, max: 23 },
  secondPostDayOffset: 3,
} as const;

/** UTF-8 byte length — review replies are limited in bytes, so emoji/accents count more than 1. */
export function utf8ByteLength(text: string): number {
  return new TextEncoder().encode(text).length;
}

/**
 * Photos sent to the Google listing. Google downloads each photo itself and accepts JPG/PNG of at least
 * 250×250 px and at most 5 MB; under `sharpEdgePx` on the long edge it may look blurry. The web converts
 * and resizes before upload (`maxEdgePx`), the API checks the real bytes. Google ranks listings with fresh
 * photos higher — the page nudges the owner every `cadenceDays`.
 */
export const GBP_PHOTO_LIMITS = {
  minEdgePx: 250,
  sharpEdgePx: 720,
  maxEdgePx: 2048,
  maxBytes: 5 * 1024 * 1024,
  cadenceDays: 14,
} as const;

/**
 * Owner-facing reason stored on an hours fix when Google kept its own hours after "Put my hours back". The web
 * matches it to lead the card with "Review on Google" (any other error keeps "Put my hours back" first).
 */
export const GBP_GOOGLE_KEPT_HOURS_ERROR = 'Google kept its own hours. Review them on Google, or try again.';
